import { useCallback, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";

import type {
  DestinationChannel,
  DestinationCorrector,
} from "pages/Login/destination-edit";
import { confirmedDestination } from "pages/StartLead/awaitingConfirmation";
import { verifyPathFor } from "pages/StartLead/destinations";
import { handoffFor } from "pages/StartLead/handoff";
import { clearStoredState, loadState, saveState } from "pages/StartLead/state";
import { submissionIdFromSearch } from "pages/StartResume/resume";

import {
  correctionBody,
  correctionOutcomeOf,
  resultAfterCorrection,
} from "./correction";
import { postCorrection } from "./correctionClient";

/** What the visitor typed for each destination in corrections made on this
 * page, which the screen shows in place of the destination the server
 * reveals: a visitor proven by the ZIP code alone is shown it masked. */
export type TypedDestinations = Record<DestinationChannel, string | null>;

const NOTHING_TYPED: TypedDestinations = { phone: null, email: null };

function followStoredResult(replacedId: string, newId: string) {
  const state = loadState();
  const result = resultAfterCorrection(state.result, replacedId, newId);
  if (result !== null) saveState({ ...state, result });
}

/**
 * Saves a corrected phone or email for the submission in the URL, with the
 * handoff the signed link carries when there is one and the ZIP code from the
 * form when the visitor gives it. A corrected submission that needs no
 * confirmation goes on to its quote; one that does continues on the code
 * screen for its own grant. Unavailable (undefined corrector) where the URL
 * names no submission, so the screen offers no correction. The typed values
 * live in this hook's state only, so they last as long as the page does.
 * Pre-need only: the screen the visitor continues on keeps the pre-need line.
 */
export function useCorrector(search: string): {
  correct: DestinationCorrector | undefined;
  typed: TypedDestinations;
} {
  const navigate = useNavigate();
  const [typed, setTyped] = useState<TypedDestinations>(NOTHING_TYPED);
  const { submissionId, handoff } = useMemo(() => {
    const id = submissionIdFromSearch(search);
    return {
      submissionId: id,
      handoff: id === null ? null : handoffFor(search, id),
    };
  }, [search]);

  const correct = useCallback<DestinationCorrector>(
    async (channel, value, zip) => {
      if (submissionId === null)
        return { kind: "closed", reason: "not_allowed" };
      const answer = await postCorrection(
        submissionId,
        correctionBody(handoff, channel, value, zip),
      );
      const outcome = correctionOutcomeOf(answer);
      if (outcome.kind === "failed") return outcome.failure;
      if (outcome.kind === "completed") {
        clearStoredState();
        navigate(confirmedDestination(outcome.next, submissionId), {
          replace: true,
        });
        return { kind: "done", outcome: "completed" };
      }
      followStoredResult(submissionId, outcome.submissionId);
      setTyped((held) => ({ ...held, [channel]: value }));
      navigate(verifyPathFor(outcome.submissionId, outcome.verify), {
        replace: true,
        state: { loginGrant: outcome.grant.grant, leadKind: "planning" },
      });
      return { kind: "done", outcome: "awaiting_confirmation" };
    },
    [submissionId, handoff, navigate],
  );

  return { correct: submissionId === null ? undefined : correct, typed };
}
